import {
  Text,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  useDisclosure,
  Stack,
  Avatar,
  Badge,
  Box
} from "@chakra-ui/react";
import moment from "moment";
import { useRouter } from "next/router";
import { useEffect, useState, useRef } from "react";
import { FiBell, FiUser } from "react-icons/fi";
import { TbTransfer } from "react-icons/tb";
import {
  getAllNotifications,
  getLastSeen,
  setNotificationLastSeen
} from "../../controllers/notification.controller";
import { useAuth } from "../../hooks/AuthContext";
import { FaExclamation } from "react-icons/fa";
import useSmartToast from "../../hooks/SmartToast";

export default function NotificationsButton() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [lastSeen, setLastSeen] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  // Keep track of which notifications we've already surfaced via system notifications
  const surfacedKeysRef = useRef(new Set());
  // Track initial load to avoid blasting user with a backlog
  const initialLoadRef = useRef(true);
  // Track previously seen ids to detect new arrivals even if lastSeen unchanged
  const prevIdsRef = useRef(new Set());
  const toast = useSmartToast();

  const fetchNotifications = async () => {
    try {
      const data = await getAllNotifications();

      if(data?.forcedLogout) {
        logout();
        return;
      }

      // console.log(data);
      setNotifications(data?.notifications);
    } catch (error) {
      console.log(error);
      // toast.error(error, "An error occurred while fetching notifications")
    }
  };

  const setLastSeenServer = async () => {
    try {
      await setNotificationLastSeen();
    } catch (error) {
      console.log(error);
    }
  };

  const getLastSeenServer = async () => {
    try {
      const data = await getLastSeen();
      setLastSeen(new Date(data.lastSeen));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getLastSeenServer();
    fetchNotifications();

    const interval = setInterval(async () => {
      try {
        const data = await getAllNotifications();
        // console.log(data);
        if(data?.forcedLogout) {
        logout();
        return;
      }

      // console.log(data);
      setNotifications(data?.notifications);
      } catch (error) {
        console.log(error);
        clearInterval(interval);
        // toast.error(error, "An error occurred while fetching notifications")
      }
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const notificationCount =
      notifications?.filter((notification) => {
        return moment(notification.createdAt).isAfter(lastSeen);
      }).length || 0;
    // console.log(notificationCount);

    setNotificationCount(notificationCount);
  }, [notifications]);

  // Fire browser (system) notifications for newly arrived unseen notifications
  useEffect(() => {
    if (typeof window === "undefined") return; // SSR guard
    if (!("Notification" in window)) return; // Browser doesn't support
    if (!notifications || notifications.length === 0) return;

    let cancelled = false;

    const run = async () => {
      try {
        let permission = Notification.permission;
        if (permission === "default") {
          try {
            permission = await Notification.requestPermission();
          } catch (_) {
            // ignore
          }
        }
        if (permission !== "granted" || cancelled) return;

        // Detect newly added notifications (by id or composite key)
        const newlyAdded = notifications.filter((n) => {
          const id = n._id || n.id || `${n.createdAt}-${n.subject}`;
          if (prevIdsRef.current.has(id)) return false;
          prevIdsRef.current.add(id);
          return true;
        });

        // Determine which of those are unseen relative to lastSeen (if we have it)
        const candidates = newlyAdded.filter((n) => {
          if (!lastSeen) return true; // If baseline not loaded yet, defer (we'll mark initial load below)
          try {
            return moment(n.createdAt).isAfter(lastSeen);
          } catch (_) {
            return true;
          }
        });

        // On first load, don't spam user with backlog
        if (initialLoadRef.current) {
          // Just mark surfaced keys
          candidates.forEach((n) => {
            const key = `${n.createdAt}-${n.subject}`;
            surfacedKeysRef.current.add(key);
          });
          initialLoadRef.current = false;
          return;
        }

        candidates.forEach((n) => {
          const key = `${n.createdAt}-${n.subject}`;
          if (surfacedKeysRef.current.has(key)) return;
          surfacedKeysRef.current.add(key);
          try {
            const notificationTitle = n.subject || "New Notification";
            const body = n.message || "You have a new notification";
            const sysNotification = new Notification(notificationTitle, {
              body,
              icon: "/favicon-32x32.png",
              tag: key
            });
            sysNotification.onclick = (ev) => {
              ev.preventDefault();
              window.focus();
              if (n.actionLink) {
                try {
                  window.open(n.actionLink, "_blank");
                } catch (_) {}
              }
              sysNotification.close();
            };
          } catch (err) {
            // Fallback: show toast if available
            try {
              toast.error(
                n.subject || "New Notification",
                n.message || "You have a new notification"
              );
            } catch (_) {}
          }
        });
      } catch (err) {
        // console.warn("System notification effect error", err);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [notifications, lastSeen, toast]);

  return (
    <Box position="relative">
      <IconButton
        icon={<FiBell />}
        variant="ghost"
        aria-label="Notifications"
        colorScheme="gray"
        display={{ base: "none", md: "flex" }}
        onClick={onOpen}
      />
      {notificationCount > 0 && (
        <Badge
          colorScheme="orange"
          rounded="full"
          position="absolute"
          bottom="0"
          right="0"
          pointerEvents="none"
        >
          {notificationCount > 9 ? "9+" : notificationCount}
        </Badge>
      )}
      <NotificationsDrawer
        isOpen={isOpen}
        onClose={() => {
          setLastSeen(new Date());
          setLastSeenServer();
          onClose();
        }}
        notifications={notifications}
        lastSeen={lastSeen}
      />
    </Box>
  );
}

function NotificationsDrawer({ isOpen, onClose, notifications, lastSeen }) {
  // console.log(lastSeen);
  const seenNotifications = notifications?.filter((notification) =>
    moment(notification.createdAt).isSameOrBefore(lastSeen)
  );
  const unseenNotifications = notifications?.filter((notification) =>
    moment(notification.createdAt).isAfter(lastSeen)
  );

  return (
    <Drawer onClose={onClose} isOpen={isOpen} size="sm">
      <DrawerOverlay />
      <DrawerContent roundedLeft="2xl">
        <DrawerCloseButton p="4" m="4" />
        <DrawerHeader borderBottom="solid 1px" borderBottomColor="gray.200">
          <HStack px="2" py="2" spacing="4">
            <FiBell />
            <Text>Notifications</Text>
          </HStack>
        </DrawerHeader>
        <DrawerBody px="0" h="full">
          <Stack h="full" overflowY="auto" overflowX="hidden" spacing="0">
            {unseenNotifications.length > 0 && (
              <Stack>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  px="4"
                  mt="3"
                  mb="2"
                  letterSpacing="wider"
                >
                  UNSEEN NOTIFICATIONS
                </Text>
                {unseenNotifications?.map((notification, index) => (
                  <NotificationItem
                    key={index}
                    notification={notification}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                ))}
              </Stack>
            )}
            {seenNotifications.length > 0 && (
              <Stack>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  px="4"
                  mt="3"
                  mb="2"
                  letterSpacing="wider"
                >
                  OLDER NOTIFICATIONS
                </Text>
                {seenNotifications?.map((notification, index) => (
                  <NotificationItem
                    key={index}
                    notification={notification}
                    isOpen={isOpen}
                    onClose={onClose}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

function NotificationItem({ notification, isOpen, onClose }) {
  // const title = 'New Quotation';
  // const description = 'You have a new quotation from John Doe';
  // const time = '2 hours ago';

  const type = notification.type.split("_")[2]?.toLowerCase();
  const router = useRouter();

  return (
    <HStack
      spacing="4"
      borderBottom="solid 1px"
      borderBottomColor="gray.200"
      px="4"
      py="2"
      _hover={{ bg: "gray.100" }}
      transition={"all 0.25s"}
      w="full"
      // onClick={() => {
      //   router.push(notification.actionLink);
      //   onClose();
      // }}
    >
      <Avatar size="sm" bg={"blue.800"} icon={<FiBell fontSize="1rem" />} />
      <Stack spacing="0.5" w="full">
        <HStack spacing="2">
          <Text fontWeight="medium" fontSize="sm" color="gray.800">
            {notification.subject}
          </Text>
          <Text fontSize="xs" color="gray.500" ml="auto">
            {moment(notification.createdAt).fromNow()}
          </Text>
        </HStack>
        <Text fontSize="sm" color="gray.600">
          {notification.message}
        </Text>
      </Stack>
    </HStack>
  );
}
